/* ---------------------------------------------------------------------------
 * Custom Market Confirmation Modal
 * ---------------------------------------------------------------------------
 * Purpose : Decides whether to show the shipping-country confirmation modal,
 *           and applies the shopper's choice.
 * Author  : Sothiares dev team
 * Date    : 2026-08-13
 *
 * Detection strategy, cheapest-first:
 *
 *   First visit  -- uses Shopify.country, which is already inlined on every
 *                   page by Shopify. Zero network calls.
 *   Repeat visit -- normally does nothing at all. At most once every
 *                   recheck_days it fetches /browsing_context_suggestions.json
 *                   during idle time to see whether the shopper's *detected*
 *                   country has actually changed (they moved, or they are
 *                   travelling). That endpoint reports GeoIP independently of
 *                   the stored preference, which Shopify.country does not --
 *                   Shopify.country reflects the applied country, so once a
 *                   choice is stored it can never disagree with itself.
 *
 * The modal is only re-opened when detection genuinely disagrees with the
 * stored choice. Reopening on every visit is the failure mode that makes these
 * modals hated, so it is deliberately not possible here.
 * ------------------------------------------------------------------------- */

(() => {
  const DAY_MS = 24 * 60 * 60 * 1000;

  /**
   * Reads the persisted choice. Any malformed or unreadable value is treated as
   * "no choice" rather than throwing -- Safari private mode and hardened privacy
   * settings can make localStorage access itself raise.
   *
   * @param {string} key
   * @returns {{country: string, ts: number, checkedAt: number} | null}
   */
  const readChoice = (key) => {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.country !== 'string' || typeof parsed.ts !== 'number') {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

  /**
   * @param {string} key
   * @param {object} value
   */
  const writeChoice = (key, value) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage unavailable. The modal will show again next visit, which is a
      // worse experience but not a broken one. Nothing to recover here.
    }
  };

  /**
   * Asks Shopify what country it detects for this request, ignoring any stored
   * market preference.
   *
   * @returns {Promise<string | null>} ISO code, or null if unavailable.
   */
  const fetchDetectedCountry = async () => {
    try {
      const response = await fetch(
        '/browsing_context_suggestions.json?source=geolocation_recommendation&country%5Benabled%5D=true',
        { headers: { Accept: 'application/json' } }
      );
      if (!response.ok) return null;

      const data = await response.json();
      return data?.detected_values?.country?.handle ?? null;
    } catch {
      return null;
    }
  };

  /** Runs a callback when the browser is idle, falling back to a timeout. */
  const whenIdle = (callback) => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback, { timeout: 4000 });
    } else {
      window.setTimeout(callback, 2000);
    }
  };

  const init = () => {
    const dialog = document.querySelector('[data-market-modal]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    const storageKey = dialog.dataset.storageKey || 'sothiares_market';
    const rememberDays = Number(dialog.dataset.rememberDays) || 365;
    const recheckDays = Number(dialog.dataset.recheckDays) || 30;
    const openDelay = Number(dialog.dataset.openDelay) || 0;

    // Shopify.country is the applied market country; the data attribute is the
    // same value rendered server-side. Prefer the runtime value when present.
    const appliedCountry = window.Shopify?.country || dialog.dataset.detectedCountry || '';

    const select = dialog.querySelector('[data-market-modal-select]');
    const form = dialog.querySelector('form');

    // Shopify's {% form 'localization' %} emits its own return_to field. Rewrite
    // that field rather than adding a second one -- Shopify's value drops the
    // query string, which would break UTM attribution for anyone who changes
    // country mid-campaign. Only create the field if Shopify stopped emitting it.
    if (form) {
      let returnTo = form.querySelector('input[name="return_to"]');
      if (!returnTo) {
        returnTo = document.createElement('input');
        returnTo.type = 'hidden';
        returnTo.name = 'return_to';
        form.appendChild(returnTo);
      }
      returnTo.value = window.location.pathname + window.location.search;
    }

    const heading = dialog.querySelector('[data-market-modal-heading]');
    const headingTemplate = dialog.dataset.headingTemplate;

    /** Strips the trailing currency code: "Sweden (SEK)" -> "Sweden". */
    const countryNameFrom = (option) =>
      option ? option.textContent.replace(/\s*\([^)]*\)\s*$/, '').trim() : '';

    /**
     * Rebuilds the question from the raw [country] template rather than
     * pattern-matching the rendered string, so merchant copy edits in the theme
     * editor cannot silently break it.
     */
    const setHeadingCountry = (name) => {
      if (!heading || !name || !headingTemplate?.includes('[country]')) return;
      heading.textContent = headingTemplate.replace('[country]', name);
    };

    // The heading is rendered server-side from the applied country, so without
    // this it keeps asking about the old country after the shopper picks a new
    // one -- the modal contradicting the control directly above the button.
    select?.addEventListener('change', () => {
      setHeadingCountry(countryNameFrom(select.selectedOptions[0]));
    });

    const open = () => {
      if (!dialog.open) dialog.showModal();
    };

    const remember = (country) => {
      writeChoice(storageKey, {
        country,
        ts: Date.now(),
        checkedAt: Date.now(),
      });
    };

    // Esc / backdrop dismissal still counts as an answer. Storing the applied
    // country here is what stops the modal nagging someone who closed it.
    dialog.addEventListener('close', () => {
      if (!readChoice(storageKey)) remember(appliedCountry);
    });

    if (form) {
      form.addEventListener('submit', (event) => {
        const chosen = select?.value || appliedCountry;
        remember(chosen);

        // If nothing changed there is no market to switch to, so skip the POST
        // and the full page reload it causes. This is the common path.
        if (chosen === appliedCountry) {
          event.preventDefault();
          dialog.close();
        }
      });
    }

    // The theme editor should always render the modal so it can be styled.
    if (window.Shopify?.designMode) {
      open();
      return;
    }

    const stored = readChoice(storageKey);

    if (!stored) {
      window.setTimeout(open, openDelay);
      return;
    }

    // Expired outright -- treat as a first visit again.
    if (Date.now() - stored.ts > rememberDays * DAY_MS) {
      window.setTimeout(open, openDelay);
      return;
    }

    const lastChecked = stored.checkedAt ?? stored.ts;
    if (Date.now() - lastChecked < recheckDays * DAY_MS) return;

    whenIdle(async () => {
      const detected = await fetchDetectedCountry();

      // Record the attempt regardless, so a failing or blocked request cannot
      // turn into a fetch on every single page view.
      writeChoice(storageKey, { ...stored, checkedAt: Date.now() });

      if (!detected || detected === stored.country) return;

      if (select) select.value = detected;

      // Re-point the heading at the newly detected country. Without this the
      // modal would ask "Are you shopping in <old country>?" -- the opposite of
      // what just triggered it.
      setHeadingCountry(countryNameFrom(select?.querySelector(`option[value="${CSS.escape(detected)}"]`)));

      open();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
